const TOOL_AUTH_CODE_QUERY_KEY = 'tool_auth_code'
const OAUTH_CALLBACK_QUERY_KEYS = [
  TOOL_AUTH_CODE_QUERY_KEY,
  'state',
  'error',
]

/**
 * 从 URL 参数中读取指定 key 的最后一个非空值。
 * @param params URLSearchParams 实例。
 * @param key 参数名。
 * @returns 最后一个非空参数值；没有可用值时返回空字符串。
 */
function readLastParam(params: URLSearchParams, key: string): string {
  const values = params.getAll(key)
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index]
    if (value && value.trim()) {
      return value.trim()
    }
  }
  return ''
}

/**
 * 规范化 OAuth 登录成功后的站内回跳路径。
 * @param raw 外部传入的回跳路径。
 * @returns 安全的站内路径；非法路径统一返回根路径。
 */
export function normalizeReturnToPath(raw: string): string {
  const source = String(raw || '').trim()
  if (!source) return '/'

  let value = source
  try {
    value = decodeURIComponent(source)
  } catch {
    value = source
  }

  if (!value.startsWith('/')) return '/'
  if (value.startsWith('//')) return '/'
  return value
}

export type OAuthRedirectCallbackPayload = {
  state: string
  error: string
  toolAuthCode: string
}

/**
 * 从当前地址读取 Passport OAuth 回调参数。
 * @param locationLike 可测试替换的 location 对象。
 * @returns 存在回调参数时返回回调载荷，否则返回 null。
 */
export function readOAuthCallbackPayloadFromLocation(
  locationLike: Pick<Location, 'search'> = window.location,
): OAuthRedirectCallbackPayload | null {
  const params = new URLSearchParams(locationLike.search)
  const state = readLastParam(params, 'state')
  const error = readLastParam(params, 'error')
  const toolAuthCode = readLastParam(params, TOOL_AUTH_CODE_QUERY_KEY)
  if (!state && !error && !toolAuthCode) return null

  return {
    state,
    error,
    toolAuthCode,
  }
}

/**
 * 从当前地址栏移除 OAuth 回调参数。
 */
export function clearAuthCallbackParamsFromCurrentUrl(): void {
  if (typeof window === 'undefined' || typeof history === 'undefined') return
  const url = new URL(window.location.href)
  removeAuthCallbackParams(url)
  const nextPath = `${url.pathname}${url.search}${url.hash}`
  history.replaceState(history.state, document.title, nextPath || '/')
}

/**
 * 立即并在下一个宏任务再次清理 OAuth 回调参数。
 */
export function clearAuthCallbackParamsSoon(): void {
  clearAuthCallbackParamsFromCurrentUrl()
  window.setTimeout(() => {
    clearAuthCallbackParamsFromCurrentUrl()
  }, 0)
}

/**
 * 从指定 URL 对象中移除 OAuth 回调参数。
 * @param url 需要清理的 URL 对象。
 * @returns 已移除回调参数的同一个 URL 对象。
 */
export function removeAuthCallbackParams(url: URL): URL {
  for (const key of OAUTH_CALLBACK_QUERY_KEYS) {
    url.searchParams.delete(key)
  }
  if (url.hash.includes('=')) {
    url.hash = ''
  }
  return url
}

/**
 * 生成传给 Passport 的完整回跳地址。
 * @param returnTo 登录成功后需要回到的站内路径。
 * @returns 可传入授权页 `redirect_uri` 的完整 URL。
 */
export function buildAppReturnUrl(returnTo = ''): string {
  const base = new URL(import.meta.env.BASE_URL, window.location.origin)
  const next = normalizeReturnToPath(returnTo)
  if (!next || next === '/') {
    return removeAuthCallbackParams(base).toString()
  }
  return removeAuthCallbackParams(new URL(next.replace(/^\//, ''), base)).toString()
}
